import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box, Fade, Typography } from '@mui/material';
import { useTheme } from '@mui/material';
import useGeolocation from '../hooks/useGeolocation';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

if (typeof document !== 'undefined' && !document.getElementById('bml-map-pulse-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'bml-map-pulse-style';
  styleEl.textContent = '@keyframes bml-map-pulse { 0% { transform: scale(0.8); opacity: 0.6; } 100% { transform: scale(2.4); opacity: 0; } }';
  document.head.appendChild(styleEl);
}

if (typeof document !== 'undefined' && !document.getElementById('bml-map-loader-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'bml-map-loader-style';
  styleEl.textContent = '@keyframes bml-map-loader-ripple { 0% { width: 24px; height: 24px; opacity: 0.55; } 100% { width: 110px; height: 110px; opacity: 0; } } @keyframes bml-map-loader-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }';
  document.head.appendChild(styleEl);
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function LibraryMapLoader() {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const ringStyle = (delay) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    border: `2px solid ${primaryColor}`,
    animation: 'bml-map-loader-ripple 2.1s ease-out infinite',
    animationDelay: delay,
  });

  return (
    <Box
      sx={{
        height: 320,
        borderRadius: 2,
        overflow: 'hidden',
        mt: 2,
        mb: 2,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
          backgroundImage: `
            linear-gradient(to right, ${theme.palette.divider} 1px, transparent 1px),
            linear-gradient(to bottom, ${theme.palette.divider} 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ position: 'relative', width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={ringStyle('0s')} />
          <Box sx={ringStyle('0.7s')} />
          <Box sx={ringStyle('1.4s')} />
          <Box
            component="svg"
            width="28"
            height="36"
            viewBox="0 0 40 52"
            xmlns="http://www.w3.org/2000/svg"
            sx={{
              position: 'relative',
              zIndex: 1,
              animation: 'bml-map-loader-bounce 1.4s ease-in-out infinite',
            }}
          >
            <path
              d="M20 0 C9 0 0 9 0 20 C0 35 20 52 20 52 C20 52 40 35 40 20 C40 9 31 0 20 0 Z"
              fill={primaryColor}
              stroke="white"
              strokeWidth="2"
            />
            <path
              d="M13 14 H27 V26 H13 Z M13 14 V26 M20 14 V26"
              stroke="white"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
          Locating…
        </Typography>
      </Box>
    </Box>
  );
}

function LibraryRouteMap({ library }) {
  const { coordinates, permissionState, requestPermission } = useGeolocation();
  const [route, setRoute] = useState({ coords: null, distanceMeters: null });

  useEffect(() => {
    if (permissionState === 'granted' && !coordinates) {
      requestPermission();
    }
  }, [permissionState, coordinates, requestPermission]);

  const libLat = library ? Number(library.lat) : NaN;
  const libLng = library ? Number(library.lng) : NaN;

  const userLat = coordinates ? Number(coordinates.latitude) : NaN;
  const userLng = coordinates ? Number(coordinates.longitude) : NaN;

  const libPosValid = Number.isFinite(libLat) && Number.isFinite(libLng);
  const userPosValid = Number.isFinite(userLat) && Number.isFinite(userLng);

  if (!library || !libPosValid || permissionState !== 'granted') {
    return null;
  }

  if (!coordinates || !userPosValid) {
    return <LibraryMapLoader />;
  }

  const libPos = [libLat, libLng];
  const userPos = [userLat, userLng];

  return (
    <LibraryRouteMapInner
      libPos={libPos}
      userPos={userPos}
      libraryName={library.name}
      route={route}
      setRoute={setRoute}
    />
  );
}

function LibraryRouteMapInner({ libPos, userPos, libraryName, route, setRoute }) {
  const theme = useTheme();

  const userColor =
    theme.palette.secondary && theme.palette.secondary.main !== theme.palette.primary.main
      ? theme.palette.secondary.main
      : theme.palette.info.main;

  const libraryIcon = useMemo(() => {
    const color = theme.palette.primary.main;
    const html = `
      <div style="position: relative; width: 40px; height: 52px;">
        <svg width="40" height="52" viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0 C9 0 0 9 0 20 C0 35 20 52 20 52 C20 52 40 35 40 20 C40 9 31 0 20 0 Z"
                fill="${color}" stroke="white" stroke-width="2"/>
          <path d="M13 14 H27 V26 H13 Z M13 14 V26 M20 14 V26"
                stroke="white" stroke-width="2" fill="none" stroke-linecap="round"/>
        </svg>
      </div>
    `;
    return L.divIcon({ html, className: '', iconSize: [40, 52], iconAnchor: [20, 52], popupAnchor: [0, -48] });
  }, [theme.palette.primary.main]);

  const userIcon = useMemo(() => {
    const html = `
      <div style="position: relative; width: 22px; height: 22px;">
        <span style="position:absolute; inset:0; border-radius:50%; background:${userColor}; opacity:0.35; animation: bml-map-pulse 1.6s ease-out infinite;"></span>
        <span style="position:absolute; left:5px; top:5px; width:12px; height:12px; border-radius:50%; background:${userColor}; border:2px solid white; box-shadow:0 0 0 1px rgba(0,0,0,0.15);"></span>
      </div>
    `;
    return L.divIcon({ html, className: '', iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -14] });
  }, [userColor]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);

    const [libLat, libLng] = libPos;
    const [userLat, userLng] = userPos;

    fetch(
      `https://router.project-osrm.org/route/v1/driving/${userLng},${userLat};${libLng},${libLat}?overview=full&geometries=geojson`,
      { signal: controller.signal }
    )
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (
          data &&
          data.routes &&
          data.routes.length > 0 &&
          data.routes[0].geometry &&
          data.routes[0].geometry.coordinates &&
          data.routes[0].geometry.coordinates.length > 0
        ) {
          const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          const distanceMeters = data.routes[0].distance;
          setRoute({ coords, distanceMeters });
        }
      })
      .catch(() => {});

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [libPos[0], libPos[1], userPos[0], userPos[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const lineColor = theme.palette.primary.main;

  const hvMeters = haversineMeters(userPos[0], userPos[1], libPos[0], libPos[1]);
  const distanceMeters = Number.isFinite(route.distanceMeters) ? route.distanceMeters : hvMeters;
  const distanceLabel = Number.isFinite(distanceMeters) ? `${(distanceMeters / 1000).toFixed(1)} km` : null;

  return (
    <Box sx={{ position: 'relative', height: 320, borderRadius: 2, overflow: 'hidden', mt: 2, mb: 2 }}>
      <Fade in timeout={400}>
        <Box sx={{ height: '100%', width: '100%' }}>
          <MapContainer
            bounds={[libPos, userPos]}
            boundsOptions={{ padding: [40, 40] }}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={
                theme.palette.mode === 'dark'
                  ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                  : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
              }
              subdomains="abcd"
              maxZoom={19}
            />
            <Marker position={libPos} icon={libraryIcon}>
              <Popup>{libraryName}</Popup>
            </Marker>
            <Marker position={userPos} icon={userIcon}>
              <Popup>Your location</Popup>
            </Marker>
            {route.coords ? (
              <Polyline positions={route.coords} pathOptions={{ color: lineColor, weight: 4, opacity: 0.8 }} />
            ) : (
              <Polyline positions={[libPos, userPos]} pathOptions={{ color: lineColor, weight: 3, opacity: 0.5, dashArray: '6 8' }} />
            )}
          </MapContainer>
        </Box>
      </Fade>
      {distanceLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1000,
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            bgcolor: 'background.paper',
            color: 'text.primary',
            fontSize: 12,
            fontWeight: 600,
            boxShadow: 2,
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pointerEvents: 'none',
          }}
        >
          {distanceLabel}
        </Box>
      )}
    </Box>
  );
}

export default LibraryRouteMap;
